import React from 'react';
import { SettingType } from '@/types/config';
import { useSettingsPanel } from '@/hooks/useSettingsPanel';
import { configService } from '@/services/ConfigService';
import SettingsToggle from '@/components/SettingsToggle';
import SettingsSelect from '@/components/SettingsSelect';

interface DynamicSettingRendererProps {
  settingKey: string;
  setting: {
    type: SettingType;
    label: string;
    icon?: string;
    defaultValue?: boolean;
    multiple?: boolean;
    data?: Record<string, { label: string; value?: boolean }>;
  };
  tabName: string;
}

const DynamicSettingRenderer: React.FC<DynamicSettingRendererProps> = ({
  settingKey,
  setting,
  tabName
}) => {
  const { getSettingValue } = useSettingsPanel();

  const handleToggleChange = (checked: boolean) => {
    const currentSettings = configService.checkAndMapStorageData();
    const updatedSettings = { ...currentSettings, [settingKey]: checked };
    
    try {
      sessionStorage.setItem('config-settings-value-map', JSON.stringify(updatedSettings));
      window.dispatchEvent(new CustomEvent('settings-updated'));
    } catch (error) {
      console.error('Failed to save setting:', error);
    }
  };

  const handleSelectChange = (selectKey: string, checked: boolean) => {
    const currentSettings = configService.checkAndMapStorageData();
    const updatedSettings = { ...currentSettings, [selectKey]: checked };
    
    try {
      sessionStorage.setItem('config-settings-value-map', JSON.stringify(updatedSettings));
      window.dispatchEvent(new CustomEvent('settings-updated'));
    } catch (error) {
      console.error('Failed to save setting:', error);
    }
  };

  if (setting.type === SettingType.TOGGLE) {
    return (
      <SettingsToggle
        checked={getSettingValue(settingKey)}
        onChange={handleToggleChange}
        label={setting.label}
        icon={setting.icon}
      />
    );
  }

  if (setting.type === SettingType.SELECT && setting.data) {
    const options = Object.entries(setting.data).map(([selectKey, option]) => ({
      key: selectKey,
      label: option.label,
      checked: getSettingValue(selectKey)
    }));

    return (
      <SettingsSelect
        label={setting.label}
        icon={setting.icon}
        options={options}
        onChange={handleSelectChange}
      />
    );
  }

  return null;
};

export default DynamicSettingRenderer;
